// Reporte "Facturas y NC" del POS: documentos emitidos (ventas y devoluciones)
// con filtros por rango, caja, turno, cajero, tipo, producto y estado SRI.
// Las NC vienen con importes NEGATIVOS del RPC → la fila de totales da el neto.
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import {
  Caja, cajasActivas, fechaLocal, fieldCls, money, posRpc,
  ProductoMin, productosMinList, UsuarioOpt, usuariosList,
} from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

interface DocPos {
  doc_id: string; fecha: string; tipo: 'factura' | 'nota_venta' | 'nc'; codigo: string; serie: string | null;
  clave_acceso: string | null; estado_emision: string | null;
  caja: string; turno: string; cajero: string | null;
  cliente: string | null; identificacion: string | null;
  subtotal: number; iva: number; total: number; estado: string;
  referencia: string | null; motivo: string | null;
}
interface SesionOpt { id: string; codigo: string; caja: string; estado: string; fecha_apertura: string }

const hoy = () => fechaLocal();
const mesInicio = () => { const d = new Date(); return fechaLocal(new Date(d.getFullYear(), d.getMonth(), 1)); };
const fmtFecha = (s: string) => new Date(s).toLocaleString(LOCALE, { dateStyle: 'short', timeStyle: 'short' });
const ESTADOS_SRI = ['autorizado', 'encolado', 'procesando', 'por_emitir', 'rechazado', 'devuelto', 'error'];

export function DocumentosPos() {
  const dlg = useDialogoEnLinea();
  const [desde, setDesde] = useState(mesInicio());
  const [hasta, setHasta] = useState(hoy());
  const [caja, setCaja] = useState('');
  const [sesion, setSesion] = useState('');
  const [cajero, setCajero] = useState('');
  const [tipo, setTipo] = useState('todos');
  const [producto, setProducto] = useState('');
  const [estadoSri, setEstadoSri] = useState('');
  const [q, setQ] = useState('');   // búsqueda libre: código, cliente, identificación

  const { data: cajas = [] } = useQuery<Caja[]>({ queryKey: ['pos-cajas-activas'], queryFn: cajasActivas });
  const { data: usuarios = [] } = useQuery<UsuarioOpt[]>({ queryKey: ['pos-usuarios'], queryFn: usuariosList });
  const { data: productos = [] } = useQuery<ProductoMin[]>({ queryKey: ['pos-productos-min'], queryFn: productosMinList });
  const { data: sesiones = [] } = useQuery<SesionOpt[]>({
    queryKey: ['pos-sesiones-filtro', caja, desde, hasta],
    queryFn: () => posRpc<SesionOpt[]>('pos_sesiones_listar', { _caja: caja || null, _desde: desde || null, _hasta: hasta || null }),
    staleTime: 0,
  });

  const qc = useQueryClient();
  const anular = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      posRpc<{ codigo: string }>('pos_nota_venta_anular', { _venta_id: id, _motivo: motivo }),
    onSuccess: (r) => { toast.success(`Nota de venta ${r.codigo} anulada (stock repuesto)`); void qc.invalidateQueries({ queryKey: ['pos-docs'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pedirAnulacion = async (d: DocPos) => {
    const v = await dlg.pedir(`Anular la nota de venta ${d.codigo}`, [
      { key: 'motivo', label: 'Motivo', minimo: 3 },
    ], { detalle: `Total ${money(d.total)}. El stock se repone y el turno debe seguir abierto.`, aceptar: 'Anular' });
    if (v?.motivo.trim()) anular.mutate({ id: d.doc_id, motivo: v.motivo.trim() });
  };

  const { data: docs = [], isFetching } = useQuery<DocPos[]>({
    queryKey: ['pos-docs', desde, hasta, caja, sesion, cajero, tipo, producto, estadoSri],
    queryFn: () => posRpc<DocPos[]>('pos_docs_listar', {
      _desde: desde || null, _hasta: hasta || null, _caja: caja || null, _sesion: sesion || null,
      _cajero: cajero || null, _tipo: tipo, _producto: producto || null, _estado_sri: estadoSri || null,
    }),
    staleTime: 0,
  });

  const filtradas = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return docs;
    return docs.filter((d) =>
      d.codigo.toLowerCase().includes(t) || (d.cliente ?? '').toLowerCase().includes(t) ||
      (d.identificacion ?? '').includes(t) || (d.serie ?? '').includes(t) || (d.referencia ?? '').toLowerCase().includes(t));
  }, [docs, q]);

  const getVal = useCallback((d: DocPos, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return d.fecha;
      case 'tipo': return d.tipo;
      case 'codigo': return d.codigo;
      case 'serie': return d.serie;
      case 'cliente': return d.cliente;
      case 'caja': return d.caja;
      case 'turno': return d.turno;
      case 'cajero': return d.cajero;
      case 'subtotal': return Number(d.subtotal);
      case 'iva': return Number(d.iva);
      case 'total': return Number(d.total);
      case 'estado': return d.estado;
      case 'sri': return d.estado_emision;
      default: return null;
    }
  }, []);
  const sort = useSort(filtradas, getVal);
  const filas = sort.sorted;

  const tSub = filas.reduce((s, d) => s + Number(d.subtotal), 0);
  const tIva = filas.reduce((s, d) => s + Number(d.iva), 0);
  const tTot = filas.reduce((s, d) => s + Number(d.total), 0);

  const headers = ['N°', 'Fecha', 'Tipo', 'Código', 'Serie', 'Cliente', 'Identificación', 'Caja', 'Turno', 'Cajero', 'Subtotal', 'IVA', 'Total', 'Estado', 'SRI', 'Ref. factura', 'Motivo', 'Clave de acceso'];
  const filasExport: Celda[][] = [
    ...filas.map((d, i) => [i + 1, fmtFecha(d.fecha), d.tipo === 'nc' ? 'Nota de crédito' : d.tipo === 'nota_venta' ? 'Nota de venta' : 'Factura', d.codigo, d.serie, d.cliente, d.identificacion, d.caja, d.turno, d.cajero, Number(d.subtotal), Number(d.iva), Number(d.total), d.estado, d.estado_emision, d.referencia, d.motivo, d.clave_acceso] as Celda[]),
    ['', '', '', '', '', '', '', '', '', `Totales (${filas.length})`, tSub, tIva, tTot, '', '', '', '', ''] as Celda[],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Facturas y NC del POS</h1>
        <ExportarBotones nombre="pos_facturas_nc" titulo="Facturas y NC del POS" subtitulo={`Del ${desde} al ${hasta}`}
          headers={headers} filas={filasExport} colsDerecha={[0, 10, 11, 12]}
          fmtPDF={(v, col) => (col >= 10 && col <= 12 && v !== '' ? money(Number(v)) : String(v ?? ''))}
          disabled={!filas.length} />
      </div>

      <Card><CardContent className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 pt-4 items-end">
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Desde</span>
          <input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Hasta</span>
          <input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Tipo</span>
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="todos">Todos</option><option value="factura">Facturas</option><option value="nota_venta">Notas de venta</option><option value="nc">Notas de crédito</option>
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Caja</span>
          <select className={fieldCls} value={caja} onChange={(e) => { setCaja(e.target.value); setSesion(''); }}>
            <option value="">— Todas —</option>{cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Turno</span>
          <select className={fieldCls} value={sesion} onChange={(e) => setSesion(e.target.value)}>
            <option value="">— Todos —</option>
            {sesiones.map((s) => <option key={s.id} value={s.id}>{s.codigo} · {s.caja} ({s.estado})</option>)}
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Cajero</span>
          <select className={fieldCls} value={cajero} onChange={(e) => setCajero(e.target.value)}>
            <option value="">— Todos —</option>{usuarios.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Producto</span>
          <select className={fieldCls} value={producto} onChange={(e) => setProducto(e.target.value)}>
            <option value="">— Todos —</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Estado SRI</span>
          <select className={fieldCls} value={estadoSri} onChange={(e) => setEstadoSri(e.target.value)}>
            <option value="">— Todos —</option>{ESTADOS_SRI.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
        <label className="block space-y-1 col-span-2 md:col-span-4 xl:col-span-8"><span className="text-xs text-muted-foreground">Buscar (código, cliente, identificación, serie)</span>
          <input className={fieldCls} placeholder="VTA-000001, NC-, nombre del cliente…" value={q} onChange={(e) => setQ(e.target.value)} /></label>
      </CardContent></Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          Documentos {isFetching && <Loader2 className="w-4 h-4 animate-spin" />}
          <span className="ml-auto text-xs font-normal text-muted-foreground">Las NC restan: la fila de totales es el neto</span>
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="fecha" sort={sort}>Fecha</SortTh>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="codigo" sort={sort}>Código</SortTh>
              <SortTh col="serie" sort={sort}>Serie</SortTh>
              <SortTh col="cliente" sort={sort}>Cliente</SortTh>
              <SortTh col="caja" sort={sort}>Caja</SortTh>
              <SortTh col="turno" sort={sort}>Turno</SortTh>
              <SortTh col="cajero" sort={sort}>Cajero</SortTh>
              <SortTh col="subtotal" sort={sort} align="right">Subtotal</SortTh>
              <SortTh col="iva" sort={sort} align="right">IVA</SortTh>
              <SortTh col="total" sort={sort} align="right">Total</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <SortTh col="sri" sort={sort}>SRI</SortTh>
              <th className="py-2 pr-3"></th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && <tr><td colSpan={15} className="py-10 text-center text-muted-foreground">{isFetching ? 'Consultando…' : 'Sin documentos con esos filtros'}</td></tr>}
              {filas.map((d, i) => (
                <tr key={`${d.tipo}-${d.codigo}`} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{fmtFecha(d.fecha)}</td>
                  <td className="py-1.5 pr-3">
                    {d.tipo === 'nc'
                      ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300" title={`Factura: ${d.referencia ?? '—'} · Motivo: ${d.motivo ?? '—'}`}>NC</span>
                      : d.tipo === 'nota_venta'
                        ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title="Nota de venta — documento interno no tributario">Nota venta</span>
                        : <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Factura</span>}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{d.codigo}{d.referencia ? <span className="text-muted-foreground"> → {d.referencia}</span> : ''}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs" title={d.clave_acceso ?? 'Sin clave de acceso'}>{d.serie ?? '—'}</td>
                  <td className="py-1.5 pr-3 max-w-[14rem] truncate" title={`${d.cliente ?? ''} (${d.identificacion ?? 's/i'})`}>{d.cliente ?? '—'}</td>
                  <td className="py-1.5 pr-3">{d.caja}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{d.turno}</td>
                  <td className="py-1.5 pr-3">{d.cajero ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(d.subtotal)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(d.iva)}</td>
                  <td className={`py-1.5 pr-3 text-right font-mono tabular-nums font-medium ${Number(d.total) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{money(d.total)}</td>
                  <td className="py-1.5 pr-3 text-xs">{d.estado}</td>
                  <td className="py-1.5 pr-3 text-xs">{d.estado_emision ?? '—'}</td>
                  <td className="py-1.5 pr-2 text-right">
                    {d.tipo === 'nota_venta' && d.estado === 'registrada' && (
                      <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Anular esta nota de venta (repone stock; exige turno abierto)"
                        disabled={anular.isPending} onClick={() => void pedirAnulacion(d)}>
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filas.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={9}>Totales ({filas.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tSub)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tIva)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tTot)}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
